/**
 * @boli/core - Notification Service
 *
 * Platform-agnostic notification logic for Boli Live.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Key Features:
 * - Fetch user notifications (paginated)
 * - Get unread count
 * - Mark notifications as read
 * - Subscribe to real-time notifications via PocketBase
 */

import type {
  Notification,
  NotificationListResponse,
  UnreadCountResponse,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface INotificationService {
  /**
   * Get user's notifications (paginated)
   */
  getNotifications(params?: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
  }): Promise<NotificationListResponse>;

  /**
   * Get unread notification count
   */
  getUnreadCount(): Promise<number>;

  /**
   * Mark a single notification as read
   */
  markAsRead(notificationId: string): Promise<boolean>;

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Promise<{ success: boolean; markedAsRead: number }>;
}

export interface NotificationServiceConfig {
  apiBaseUrl: string;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_NOTIFICATION_CONFIG: NotificationServiceConfig = {
  apiBaseUrl: '',
};

// ============================================================================
// NOTIFICATION SERVICE IMPLEMENTATION
// ============================================================================

export class NotificationService implements INotificationService {
  private config: NotificationServiceConfig;
  private fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  private getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<NotificationServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_NOTIFICATION_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Get authorization headers
   */
  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Get user's notifications (paginated)
   */
  async getNotifications(params?: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
  }): Promise<NotificationListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.set('limit', params.limit.toString());
      if (params?.offset) queryParams.set('offset', params.offset.toString());
      if (params?.unreadOnly) queryParams.set('unreadOnly', 'true');

      const url = `${this.config.apiBaseUrl}/api/notifications${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await this.fetchAdapter(url, {
        headers: await this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();

      // Map the response to our Notification type
      const notifications: Notification[] = (data.notifications || []).map((n: Record<string, unknown>) => ({
        id: n.id as string,
        userId: n.userId as string,
        type: n.type as Notification['type'],
        title: n.title as string,
        content: n.content as string,
        relatedId: n.relatedId as string | undefined,
        relatedType: n.relatedType as Notification['relatedType'] | undefined,
        isRead: n.isRead as boolean,
        metadata: n.metadata as Record<string, unknown> | undefined,
        createdAt: (n.created as string) || new Date().toISOString(),
      }));

      return {
        notifications,
        count: data.count || notifications.length,
      };
    } catch (error) {
      console.error('Failed to get notifications:', error);
      return { notifications: [], count: 0 };
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/notifications/unread-count`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        return 0;
      }

      const data: UnreadCountResponse = await response.json();
      return data.unreadCount || 0;
    } catch (error) {
      console.error('Failed to get unread count:', error);
      return 0;
    }
  }

  /**
   * Mark a single notification as read
   */
  async markAsRead(notificationId: string): Promise<boolean> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/notifications/${notificationId}/read`,
        {
          method: 'PUT',
          headers: await this.getHeaders(),
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ success: boolean; markedAsRead: number }> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/notifications/read-all`,
        {
          method: 'PUT',
          headers: await this.getHeaders(),
        }
      );

      if (!response.ok) {
        return { success: false, markedAsRead: 0 };
      }

      const data = await response.json();
      return {
        success: data.success || false,
        markedAsRead: data.markedAsRead || 0,
      };
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      return { success: false, markedAsRead: 0 };
    }
  }
}

// ============================================================================
// NOTIFICATION SERVICE FACTORY
// ============================================================================

let notificationServiceInstance: NotificationService | null = null;

export function createNotificationService(
  config: Partial<NotificationServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): NotificationService {
  notificationServiceInstance = new NotificationService(config, fetchAdapter, getAuthToken);
  return notificationServiceInstance;
}

export function getNotificationService(): NotificationService {
  if (!notificationServiceInstance) {
    throw new Error('NotificationService not initialized. Call createNotificationService first.');
  }
  return notificationServiceInstance;
}
